import React, { useEffect, useRef } from 'react'
import ReactQuill, { Quill } from 'react-quill'
import styled from 'styled-components'
import 'react-quill/dist/quill.snow.css' // Import Quill theme
import './Text.scss' // Import our overrides after Quill theme

// =======================================
// 1. Register Custom Formats with Safe Tokens
// =======================================

// Define fonts with safe tokens, display labels, and actual CSS fontFamily values.
const fonts = [
  { value: 'mulish', label: 'Mulish', fontFamily: '"Mulish", sans-serif' },
  {
    value: 'arialblack',
    label: 'Arial Black',
    fontFamily: '"Arial Black", sans-serif'
  },
  {
    value: 'timesnewroman',
    label: 'Times New Roman',
    fontFamily: '"Times New Roman", serif'
  },
  { value: 'georgia', label: 'Georgia', fontFamily: 'Georgia, serif' },
  {
    value: 'couriernew',
    label: 'Courier New',
    fontFamily: '"Courier New", monospace'
  },
  {
    value: 'helvetica',
    label: 'Helvetica',
    fontFamily: 'Helvetica, sans-serif'
  }
]
const fontWhitelist = fonts.map(font => font.value)

// Register the Font format with Quill using our safe tokens.
const Font = Quill.import('formats/font')
Font.whitelist = fontWhitelist
Quill.register(Font, true)

// Define size and line-height options.
const sizeList = [
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '24px',
  '36px',
  '48px'
]
const lineHeightList = ['1.0', '1.2', '1.5', '1.8', '2.0', '2.4']

const Parchment = Quill.import('parchment')

// Use style attributors instead of class attributors
const StyleAttributor = Parchment.Attributor.Style

// Configure line height with inline styles
const LineHeightStyle = new StyleAttributor(
  'lineheight',
  'line-height',
  {
    scope: Parchment.Scope.BLOCK,
    whitelist: lineHeightList
  }
)
Quill.register(LineHeightStyle, true)

// Configure size format to use inline styles
const Size = Quill.import('attributors/style/size')
Size.whitelist = sizeList
Quill.register(Size, true)

// =======================================
// 2. Toolbar Configuration with Custom Handlers
// =======================================
const toolbar = {
  container: [
    // Font dropdown using safe tokens.
    [{ font: fontWhitelist }],
    // Font size dropdown.
    [{ size: sizeList }],
    // Basic styling.
    ['bold', 'italic', 'underline', 'strike'],
    // Script options.
    [{ script: 'sub' }, { script: 'super' }],
    // Color and background.
    [{ color: [] }, { background: [] }],
    // Alignment, list, and indentation.
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    // Custom line-height dropdown (key is 'lineheight').
    [{ lineheight: lineHeightList }],
    // Blockquote, code block, and link.
    ['blockquote', 'code-block'],
    ['link']
  ],
  handlers: {
    // Custom lineheight handler.
    lineheight: function (value) {
      const quill = this.quill
      if (value) {
        quill.format('lineheight', value)
      }
    },
    // Custom color handler.
    color: function (value) {
      const quill = this.quill
      if (value === 'color-picker') {
        let picker = document.getElementById('color-picker')
        if (!picker) {
          picker = document.createElement('input')
          picker.id = 'color-picker'
          picker.type = 'color'
          picker.addEventListener('change', function () {
            quill.format('color', picker.value)
          })
          const colorItems = document.querySelectorAll(
            `[data-value='color-picker']`
          )
          const colorItem = Object.values(colorItems).find(item =>
            item.classList.contains('ql-picker-item')
          )
          if (colorItem) {
            colorItem.appendChild(picker)
          }
        }
        picker.click()
      } else {
        quill.format('color', value)
      }
    },
    // Custom font handler to update the editor and toolbar label.
    font: function (value) {
      const quill = this.quill
      if (value) {
        quill.format('font', value) // Apply font family to the editor
        const container = document.getElementById('editor-container')
        const pickerLabel =
          container &&
          container.querySelector('.ql-picker.ql-font .ql-picker-label')

        if (pickerLabel) {
          const selectedFont = fonts.find(f => f.value === value)
          if (selectedFont) {
            pickerLabel.style.fontFamily = selectedFont.fontFamily
            pickerLabel.textContent = selectedFont.label
          }
        }
      }
    },
    // Custom size handler using inline styles.
    size: function (value) {
      const quill = this.quill
      if (value) {
        // Apply inline font-size style
        quill.format('size', value)
      }
    }
  }
}

// =======================================
// 3. Styled Components: Force Overrides for Font & Line-Height
// =======================================
const QuillWrapper = styled.div`
  /* Give our container a unique id for reliable querying */
  #editor-container & {
    /* Ensures our container is unique */
  }

  .ql-editor {
    font-family: 'Mulish', Arial, 'Times New Roman', Georgia, 'Courier New',
      Helvetica, sans-serif;
    line-height: 1.5;
    word-break: break-word;
    padding: 0;
    color: black;
    border: 1px solid #e3e3e3;
    min-height: 20rem;
  }

  /* Override Quill's default heading styles from snow theme */
  .ql-editor h1 {
    font-size: inherit !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  
  .ql-editor h2 {
    font-size: inherit !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  
  .ql-editor h3 {
    font-size: inherit !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  
  .ql-editor h4, .ql-editor h5, .ql-editor h6 {
    font-size: inherit !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  /* Force editor content to use the proper font classes */
  .ql-editor .ql-font-mulish {
    font-family: 'Mulish', sans-serif !important;
  }
  .ql-editor .ql-font-arialblack {
    font-family: 'Arial Black', sans-serif !important;
  }
  .ql-editor .ql-font-timesnewroman {
    font-family: 'Times New Roman', serif !important;
  }
  .ql-editor .ql-font-georgia {
    font-family: Georgia, serif !important;
  }
  .ql-editor .ql-font-couriernew {
    font-family: 'Courier New', monospace !important;
  }
  .ql-editor .ql-font-helvetica {
    font-family: Helvetica, sans-serif !important;
  }

  /* Remove default "Sans Serif" from font label */
  .ql-picker.ql-font .ql-picker-label::before {
    content: '' !important;
  }

  /* Font dropdown: Force each option to display its label in its actual font */
  .ql-picker.ql-font .ql-picker-item::before {
    display: inline-block;
  }
  .ql-font .ql-picker-label {
    white-space: nowrap;
  }
  .ql-picker.ql-font .ql-picker-item[data-value='mulish']::before {
    content: 'Mulish';
    font-family: 'Mulish', sans-serif;
  }
  .ql-picker.ql-font .ql-picker-item[data-value='arialblack']::before {
    content: 'Arial Black';
    font-family: 'Arial Black', sans-serif;
  }
  .ql-picker.ql-font .ql-picker-item[data-value='timesnewroman']::before {
    content: 'Times New Roman';
    font-family: 'Times New Roman', serif;
  }
  .ql-picker.ql-font .ql-picker-item[data-value='georgia']::before {
    content: 'Georgia';
    font-family: Georgia, serif;
  }
  .ql-picker.ql-font .ql-picker-item[data-value='couriernew']::before {
    content: 'Courier New';
    font-family: 'Courier New', monospace;
  }
  .ql-picker.ql-font .ql-picker-item[data-value='helvetica']::before {
    content: 'Helvetica';
    font-family: Helvetica, sans-serif;
  }

  /* --- Line-Height Dropdown --- */
  /* Force the line-height dropdown items to display their numeric value */
  .ql-picker.ql-lineheight .ql-picker-item::before {
    content: attr(data-value);
    font-size: 14px;
    color: #000 !important;
  }

  .ql-container {
    z-index: 1;
  }

  .ql-toolbar,
  .ql-container {
    border: 0 !important;
  }

  .ql-toolbar.ql-snow {
    background-color: #f4f5fa;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  /* Force h1, h2, h3 elements to respect font-size attributes */
  .ql-editor h1, 
  .ql-editor h2, 
  .ql-editor h3 {
    font-size: inherit;
  }

  /* Make sure inline styles take precedence */
  .ql-editor [style*="font-size"] {
    font-size: unset !important; /* Use browser's computed font-size */
  }

  /* Add more specific header override rules */
  .ql-editor h1[style*="font-size"], 
  .ql-editor h2[style*="font-size"], 
  .ql-editor h3[style*="font-size"] {
    font-size: unset !important;
  }

  /* Size dropdown display */
  .ql-picker.ql-size .ql-picker-label::before {
    content: 'Size';
  }
  
  /* Override the default content when a size is selected */
  .ql-picker.ql-size .ql-picker-label[data-value]::before {
    content: attr(data-value) !important;
  }

  .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before {
    content: '12px';
    font-size: 12px;
  }
  .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before {
    content: '14px';
    font-size: 14px;
  }
  .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before {
    content: '16px';
    font-size: 16px;
  }
  .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
    content: '18px';
    font-size: 18px;
  }
  .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before {
    content: '20px';
    font-size: 20px;
  }
  .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before {
    content: '24px';
    font-size: 24px;
  }
  .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before {
    content: '36px';
    font-size: 36px;
  }
  .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before {
    content: '48px';
    font-size: 48px;
  }

  /* Override size classes with inline styles */
  .ql-editor [style*="font-size: 12px"] { font-size: 12px !important; }
  .ql-editor [style*="font-size: 14px"] { font-size: 14px !important; }
  .ql-editor [style*="font-size: 16px"] { font-size: 16px !important; }
  .ql-editor [style*="font-size: 18px"] { font-size: 18px !important; }
  .ql-editor [style*="font-size: 20px"] { font-size: 20px !important; }
  .ql-editor [style*="font-size: 24px"] { font-size: 24px !important; }
  .ql-editor [style*="font-size: 36px"] { font-size: 36px !important; }
  .ql-editor [style*="font-size: 48px"] { font-size: 48px !important; }
`

// =======================================
// 4. The TextEditor Component
// =======================================
const TextEditor = ({ value, onChange }) => {
  const quillRef = useRef(null)

  // Handle preserving inline font-size styles in HTML content without DOMPurify
  const preserveInlineStyles = (htmlContent) => {
    if (!htmlContent) return '';

    // Use a basic regex approach to find and preserve font sizes
    // This regex matches style attributes with font-size
    const fontSizeRegex = /style="([^"]*font-size:\s*([^;"]+)[^"]*)"/g;

    // Replace font-size styles with Quill's size format
    let processedContent = htmlContent.replace(fontSizeRegex, (match, styleAttr, fontSize) => {
      return `style="${styleAttr}"`;
    });

    return processedContent;
  };

  // Helper to find closest matching size from our whitelist
  const findClosestSize = (fontSize) => {
    // Extract numeric value for comparison
    const numericValue = parseFloat(fontSize);
    if (isNaN(numericValue)) return null;

    // Convert to px if needed
    let sizeInPx = numericValue;
    if (fontSize.includes('em')) {
      sizeInPx = numericValue * 16; // 1em = 16px typical base
    } else if (fontSize.includes('pt')) {
      sizeInPx = numericValue * 1.333; // 1pt ≈ 1.333px
    } else if (fontSize.includes('rem')) {
      sizeInPx = numericValue * 16; // 1rem = 16px typical base
    }

    // Find closest size in our whitelist
    let closest = sizeList[0];
    let closestDiff = Math.abs(parseFloat(closest) - sizeInPx);

    for (const size of sizeList) {
      const diff = Math.abs(parseFloat(size) - sizeInPx);
      if (diff < closestDiff) {
        closest = size;
        closestDiff = diff;
      }
    }

    return closest;
  };

  // Configure Quill to preserve inline styles
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();

      // Get the clipboard module to customize paste behavior
      const clipboard = quill.getModule('clipboard');

      // Override the matcher to preserve all inline styles
      if (clipboard) {
        // Match any element with style attribute and preserve it
        clipboard.addMatcher('[style*="font-size"]', (node, delta) => {
          const style = node.getAttribute('style');
          const fontSizeMatch = style && style.match(/font-size:\s*([^;]+)/i);

          if (fontSizeMatch && fontSizeMatch[1]) {
            const fontSize = fontSizeMatch[1].trim();
            const closestSize = findClosestSize(fontSize);

            if (closestSize) {
              delta.ops.forEach(op => {
                if (op.attributes === undefined) {
                  op.attributes = {};
                }
                op.attributes.size = closestSize;
              });
            }
          }

          return delta;
        });

        // Also match heading elements specifically to preserve their styles and override defaults
        ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach(tag => {
          clipboard.addMatcher(tag, (node, delta) => {
            // Get font size from style if it exists
            let fontSize;
            if (node.style && node.style.fontSize) {
              fontSize = node.style.fontSize;
            } else if (node.getAttribute && node.getAttribute('style')) {
              const style = node.getAttribute('style');
              const fontSizeMatch = style && style.match(/font-size:\s*([^;]+)/i);
              if (fontSizeMatch && fontSizeMatch[1]) {
                fontSize = fontSizeMatch[1].trim();
              }
            }

            // If no font size is specified in the heading, don't apply any size
            // This ensures our editor defaults or user selections take precedence
            if (!fontSize) {
              return delta;
            }

            // Apply the closest matching size
            const closestSize = findClosestSize(fontSize);
            if (closestSize) {
              delta.ops.forEach(op => {
                if (op.attributes === undefined) {
                  op.attributes = {};
                }
                op.attributes.size = closestSize;
              });
            }

            return delta;
          });
        });
      }
    }
  }, []);

  // Process initial value before passing to Quill
  const processedValue = value ? preserveInlineStyles(value) : '';

  // Handle changes while preserving format data
  const handleChange = (newContent) => {
    if (onChange) {
      onChange(newContent);
    }
  };

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor()
      // Set default formatting for empty editor
      if (quill.getLength() <= 1) {
        quill.format('font', 'mulish')
        quill.format('size', '16px')
      }

      // Get the current font and size at the start of the editor content
      const length = quill.getLength()
      if (length > 0) {
        const format = quill.getFormat(0, 1) // Get format at the first character
        const currentFont = format.font || 'mulish' // Default to 'mulish' if none found
        const currentSize = format.size || '16px'   // Default to 16px if none found

        // Update the toolbar labels based on current formats
        const container = document.getElementById('editor-container')

        // Update font label
        const fontPickerLabel = container && container.querySelector('.ql-picker.ql-font .ql-picker-label')
        if (fontPickerLabel) {
          const selectedFont = fonts.find(f => f.value === currentFont)
          if (selectedFont) {
            fontPickerLabel.style.fontFamily = selectedFont.fontFamily
            fontPickerLabel.textContent = selectedFont.label
          }
        }

        // Update size label - only set the data-value attribute, not the textContent
        const sizePickerLabel = container && container.querySelector('.ql-picker.ql-size .ql-picker-label')
        if (sizePickerLabel) {
          sizePickerLabel.setAttribute('data-value', currentSize)
          // Don't set textContent, as it will duplicate with the ::before content
        }
      }
    }
  }, [])

  return (
    <QuillWrapper id="editor-container">
      <ReactQuill
        preserveWhitespace
        ref={quillRef}
        value={processedValue || value}
        onChange={handleChange}
        modules={{ toolbar }}
      />
    </QuillWrapper>
  )
}

export default TextEditor
export { toolbar, sizeList }
